import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick, getCurrentInstance } from 'vue';
import Sortable from 'sortablejs';
import { useCookies } from './composables/useCookies';
import { useDocumentTypes } from './composables/useDocumentTypes';
import { buildCollectionsEndpoint, mergeCollectionPages } from './composables/useCollectionPagination';
import { parseShareToken, loadSharedCollections, rememberSharedCollection, forgetSharedCollection } from './composables/useSharedCollections';

/**
 * Favorites Handler Composable
 * Manages adding/removing items from favorites (cart) and syncing with backend.
 */
export function useFavoritesHandler(props = {}) {
    // State
    const carts = ref([]);
    const activeCart = ref(null);
    const cartNotesTextarea = ref(null);
    const loading = ref(false);
    const isOpen = ref(false);
    const editingName = ref(false); // UI state helper
    const isLoggedOut = ref(false); // Set to true on 401 to prevent reload loops
    const viewMode = ref('grid'); // 'list' or 'grid'

    // Cart Count Persistence
    const cartCount = ref(parseInt(localStorage.getItem('favorites_count') || 0));

    const updateLocalCount = (count) => {
        cartCount.value = count;
        localStorage.setItem('favorites_count', count);
        window.dispatchEvent(new CustomEvent('favorites-count-updated', { detail: count }));
    };

    const serverActiveCart = computed(() => carts.value.find(c => c.is_selected));

    // A collection opened from someone else's share link: viewable and
    // exportable, but never written to.
    const isReadOnly = computed(() => Boolean(activeCart.value?.is_read_only));

    // Optimistic overlay, keyed by item key: the favourite state the user last
    // asked for. A heart tap flips it immediately; the entry is dropped once the
    // write settles, which hands the answer back to server state.
    const optimisticFavorites = ref(new Map());
    // At most one in-flight favourite request per item. Further taps record the
    // newly wanted state instead of racing a second request against the first.
    const favoriteOps = new Map();

    const favoriteKey = (itemType, itemId) => {
        const id = String(itemId ?? '').trim();

        return itemType && id && id !== 'undefined' && id !== 'null' ? `${itemType}|${id}` : '';
    };

    const setOptimisticFavorite = (key, value) => {
        const next = new Map(optimisticFavorites.value);
        if (value === null) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
        optimisticFavorites.value = next;
    };

    /** The favourites count as the user sees it: server rows plus unsettled taps. */
    const favoritesItemCount = computed(() => {
        const items = serverActiveCart.value?.items || [];
        const savedKeys = new Set(items.map(item => favoriteKey(item.item_type, item.item_id)));

        let count = items.length;
        optimisticFavorites.value.forEach((isFavorite, key) => {
            if (isFavorite && !savedKeys.has(key)) count += 1;
            if (!isFavorite && savedKeys.has(key)) count -= 1;
        });

        return Math.max(0, count);
    });

    // Filter State
    const selectionFilter = ref('');
    const selectionSort = ref('updated_desc');
    const collectionPage = ref(1);
    const hasMoreCollections = ref(false);
    const loadingMoreCollections = ref(false);
    const collectionScrollContainer = ref(null);
    const collectionLoadMoreTrigger = ref(null);
    const collectionOptions = ref([]);
    let collectionObserver = null;
    let collectionSearchTimer = null;
    let collectionRequestId = 0;
    let cartDetailsRequestId = 0;

    const savedSelections = computed(() => carts.value.filter(c => !c.is_selected));

    const currentUser = ref(null);

    // Cookie + token persistence is split into a standalone composable
    // so the reactive closure here stays focused on cart state.
    const {
        setCookie,
        getCookie,
        removeCookie,
        saveApiToken,
        clearApiToken,
        getApiToken,
    } = useCookies();

    const exchangeToken = async (tempToken) => {
        try {
            const apiBase = document.querySelector('meta[name="api-url"]')?.getAttribute('content') || '/api';
            const baseUrl = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;

            const response = await fetch(`${baseUrl}/portal/exchange-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ token: tempToken })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.token) {
                    saveApiToken(data.token);

                    if (data.user) {
                        currentUser.value = data.user;
                    }

                    const url = new URL(window.location);
                    if (url.searchParams.has('api_token')) {
                        url.searchParams.delete('api_token');
                        window.history.replaceState({}, document.title, url);
                    }
                    console.log('[FavoritesAPI] Token exchanged successfully.');
                }
            } else {
                console.warn('[FavoritesAPI] Failed to exchange one-time token.');
                clearApiToken();
            }
        } catch (e) {
            console.error('[FavoritesAPI] Error exchanging token:', e);
            clearApiToken();
        }
    };

    // Guest Session State
    const guestSessionId = ref(localStorage.getItem('favorites_guest_session') || null);

    // Generate guest session if needed and user not logged in
    const ensureGuestSession = () => {
        const apiToken = getApiToken();

        if (!apiToken && !guestSessionId.value) {
            // Simple UUID v4 fallback
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                guestSessionId.value = crypto.randomUUID();
            } else {
                guestSessionId.value = 'guest-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            }
            localStorage.setItem('favorites_guest_session', guestSessionId.value);
        }
    };

    // Ensure on load
    ensureGuestSession();

    // Last known favourites, so hearts can paint before the network answers.
    const FAVORITES_CACHE_KEY = 'favorites_cached_keys';

    /** Short, non-reversible fingerprint so the cache never stores the token itself. */
    const fingerprint = (value) => {
        let hash = 5381;
        for (let i = 0; i < value.length; i++) {
            hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
        }

        return (hash >>> 0).toString(36);
    };

    /** Whose favourites the cache belongs to: the signed-in token, else the guest session. */
    const favoritesOwner = () => {
        const token = getApiToken();
        if (token) return `user:${fingerprint(token)}`;

        return guestSessionId.value ? `guest:${guestSessionId.value}` : '';
    };

    /** @returns {Set<string>|null} The cached favourite keys, when they belong to the current owner. */
    const readCachedFavoriteKeys = () => {
        try {
            const cached = JSON.parse(localStorage.getItem(FAVORITES_CACHE_KEY) || 'null');
            const owner = favoritesOwner();
            if (!owner || cached?.owner !== owner || !Array.isArray(cached.keys)) return null;

            return new Set(cached.keys);
        } catch (e) {
            return null;
        }
    };

    const writeCachedFavoriteKeys = (keys) => {
        const owner = favoritesOwner();
        if (!owner) return;

        try {
            localStorage.setItem(FAVORITES_CACHE_KEY, JSON.stringify({ owner, keys }));
        } catch (e) {
            // Storage full or blocked: hearts simply wait for the server next time.
        }
    };

    const cachedFavoriteKeys = readCachedFavoriteKeys();

    const newUuid = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `mm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    };

    const isUuid = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

    // External Link Modal State
    const showExternalModal = ref(false);
    const pendingExternalUrl = ref('');

    const confirmNavigation = (url) => {
        pendingExternalUrl.value = url;
        showExternalModal.value = true;
    };

    const cancelNavigation = () => {
        showExternalModal.value = false;
        pendingExternalUrl.value = '';
    };

    const proceedNavigation = () => {
        if (pendingExternalUrl.value) {
            window.open(pendingExternalUrl.value, '_blank');
            showExternalModal.value = false;
            pendingExternalUrl.value = '';
        }
    };

    const handleLinkClick = (event, item) => {
        if (item.item_type === 'external') {
            event.preventDefault();
            confirmNavigation(item.redirect_url);
        }
        // else allow default navigation (target="_blank" is on element)
    };

    // API Helpers
    const fetchApi = async (endpoint, options = {}, _isRetry = false) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        const apiToken = getApiToken();
        const apiBase = document.querySelector('meta[name="api-url"]')?.getAttribute('content') || '/api';

        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken || '',
            ...options.headers
        };

        // If body is FormData, delete Content-Type to let browser set boundary
        if (options.body instanceof FormData) {
            delete headers['Content-Type'];
        }

        if (apiToken) {
            headers['Authorization'] = `Bearer ${apiToken}`;
        }

        // Add Guest Session Header
        if (guestSessionId.value) {
            headers['X-Guest-Session'] = guestSessionId.value;
        }

        // Construct URL - handle if apiBase already has trailing slash or endpoint has leading slash
        const baseUrl = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
        const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const url = `${baseUrl}${path}`;

        const response = await fetch(url, {
            ...options,
            headers
        });

        if (!response.ok) {
            if (response.status === 401 && apiToken && !_isRetry) {
                // Token is stale/invalidated — clear it and retry as guest
                console.warn("[FavoritesAPI] Token expired, falling back to guest mode.");
                clearApiToken();
                isLoggedOut.value = true;
                // Ensure guest session exists for the retry
                ensureGuestSession();
                // Retry the same request without the token (guest mode)
                return fetchApi(endpoint, options, true);
            }
            // For non-401 errors, or guest 401s, or retries — just throw
            if (response.status !== 401) {
                console.error(`[FavoritesAPI] Error ${response.status}: ${response.statusText}`);
            }
            throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        // Handle 204 No Content
        if (response.status === 204) {
            return null;
        }

        return response.json();
    };

    /**
     * Sends a cart write under a fresh Idempotency-Key. A dropped connection
     * leaves the outcome unknown, so the write is replayed once under the same
     * key: the API answers a replay with the first response instead of writing
     * again.
     */
    const writeApi = async (endpoint, options = {}) => {
        const request = {
            ...options,
            headers: { ...options.headers, 'Idempotency-Key': newUuid() },
        };

        try {
            return await fetchApi(endpoint, request);
        } catch (e) {
            if (!(e instanceof TypeError)) throw e;

            return fetchApi(endpoint, request);
        }
    };

    /**
     * Folds the cart a write handed back into local state, replacing the
     * write-then-re-read-everything round trip.
     *
     * @returns {boolean} Whether a usable cart was applied.
     */
    const applyCartFromWrite = (cart) => {
        if (!cart || typeof cart !== 'object' || cart.id === undefined) return false;

        favoritesWriteCount += 1;
        storeCart(cart);

        return true;
    };

    /** Writes one cart into local state, keeping the on-screen cart in step. */
    const storeCart = (cart) => {
        const next = { items: [], ...cart };
        const index = carts.value.findIndex(candidate => String(candidate?.id) === String(cart.id));
        if (index !== -1) {
            carts.value[index] = next;
        } else {
            carts.value = [...carts.value, next];
        }

        if (activeCart.value && String(activeCart.value.id) === String(cart.id)) {
            activeCart.value = next;
        }

        nextTick(() => initSortable());
    };

    // Bumped by every applied write, so a slower read cannot overwrite a newer write.
    let favoritesWriteCount = 0;
    let favoritesLoad = null;

    /**
     * Loads only the favourites cart, for pages that just paint hearts. Much
     * lighter than fetchCarts, which reads every collection with every item.
     * Concurrent callers share one request.
     *
     * @returns {Promise<object|null>} The favourites cart.
     */
    const loadFavorites = () => {
        if (favoritesLoad) return favoritesLoad;

        favoritesLoad = (async () => {
            await exchangeUrlToken();

            const writesBefore = favoritesWriteCount;
            const data = await fetchApi('/carts?paginated=1');
            const cart = data?.active_cart;
            if (!cart || cart.id === undefined) return null;

            if (favoritesWriteCount === writesBefore) {
                storeCart(cart);
            }

            if (!activeCart.value) {
                activeCart.value = serverActiveCart.value || null;
            }

            clearSettledFavorites();

            return serverActiveCart.value || cart;
        })().finally(() => {
            favoritesLoad = null;
        });

        return favoritesLoad;
    };

    // Actions
    const moveCartName = ref('');
    const showMoveModal = ref(false);

    // ... (keep previous state)

    // Actions
    const loadCartDetails = async (cartId) => {
        const requestId = ++cartDetailsRequestId;
        const cart = await fetchApi(`/carts/${cartId}/details`);

        if (requestId !== cartDetailsRequestId) return null;

        const cartIndex = carts.value.findIndex(candidate => candidate.id === cart.id);

        if (cartIndex !== -1) {
            carts.value[cartIndex] = cart;
        }

        activeCart.value = cart;

        nextTick(() => {
            initSortable();
            autoResizeTextarea(cartNotesTextarea.value);
        });

        return cart;
    };

    const initCollectionObserver = () => {
        collectionObserver?.disconnect();

        if (typeof IntersectionObserver === 'undefined' || !collectionScrollContainer.value || !collectionLoadMoreTrigger.value) return;

        collectionObserver = new IntersectionObserver((entries) => {
            if (entries.some(entry => entry.isIntersecting)) {
                loadMoreCollections();
            }
        }, {
            root: collectionScrollContainer.value,
            rootMargin: '160px 0px',
        });

        collectionObserver.observe(collectionLoadMoreTrigger.value);
    };

    /** Swaps a pt_ one-time token from the URL (arrives after portal login redirect) for a real one. */
    const exchangeUrlToken = async () => {
        const urlToken = new URLSearchParams(window.location.search).get('api_token');
        if (!urlToken || !urlToken.startsWith('pt_')) return;

        const existingToken = getApiToken();
        if (existingToken && !existingToken.startsWith('pt_')) {
            console.log('[FavoritesAPI] Already have a valid token, skipping exchange.');
            // Clean up pt_ token from URL
            const url = new URL(window.location);
            url.searchParams.delete('api_token');
            window.history.replaceState({}, document.title, url);
        } else {
            await exchangeToken(urlToken);
        }
    };

    const fetchCarts = async ({ append = false, refreshActive = true, throwOnError = false } = {}) => {
        const requestId = ++collectionRequestId;

        if (append) {
            loadingMoreCollections.value = true;
        } else {
            loading.value = true;
        }

        try {
            await exchangeUrlToken();

            let endpoint = '/carts';

            if (appMode.value === 'selection') {
                endpoint = buildCollectionsEndpoint({
                    sort: selectionSort.value,
                    search: selectionFilter.value,
                    page: append ? collectionPage.value + 1 : 1,
                });
            }

            const data = await fetchApi(endpoint);
            if (!data) return; // Bail if fetchApi returned early (e.g. 401 / logged out)

            if (requestId !== collectionRequestId) return;

            if (appMode.value === 'selection') {
                const selectedCart = data.active_cart;
                const incomingCollections = Array.isArray(data.data) ? data.data : [];
                const currentActiveCart = activeCart.value;

                if (append) {
                    const mergedCollections = mergeCollectionPages(savedSelections.value, incomingCollections);
                    carts.value = [selectedCart, ...mergedCollections];
                } else {
                    carts.value = [selectedCart, ...incomingCollections];
                }

                if (currentActiveCart && !currentActiveCart.is_selected) {
                    const currentIndex = carts.value.findIndex(cart => cart.id === currentActiveCart.id);
                    if (currentIndex !== -1) {
                        carts.value[currentIndex] = currentActiveCart;
                    }
                }

                collectionPage.value = data.meta?.current_page || 1;
                hasMoreCollections.value = Boolean(data.meta?.has_more);

                if (!append) {
                    const hashParams = new URLSearchParams(window.location.hash.substring(1));
                    const requestedSelectionId = hashParams.get('selectionID');

                    // Keep showing a shared collection; it is not one of ours to reload.
                    const keepSharedView = currentActiveCart?.is_read_only && !requestedSelectionId;
                    const targetCartId = requestedSelectionId || (currentActiveCart?.is_selected === false ? currentActiveCart.id : savedSelections.value[0]?.id);

                    if (keepSharedView) {
                        activeCart.value = currentActiveCart;
                    } else if (targetCartId && refreshActive) {
                        await loadCartDetails(targetCartId);
                    } else if (targetCartId && currentActiveCart?.id == targetCartId) {
                        activeCart.value = currentActiveCart;
                    } else if (savedSelections.value.length === 0 && !selectionFilter.value.trim()) {
                        await createCart('My Collection');
                        return;
                    } else {
                        activeCart.value = selectedCart;
                    }
                }
            } else {
                carts.value = Array.isArray(data) ? data : [];

                // Define "Active Cart" vs "Viewing Cart"
            // For now, we reuse activeCart to mean "Viewing Cart", but we know the API enforces "Cart" is the only one receiving items effectively if we were to select it.
            // But wait, addItem uses activeCart.id. If we view a "Selection", addItem adds to it.
            // User requirement: "no other cart can be active" (receiving items?). 
            // If I view "Bedroom 1" and click "Add Web Link", it should probably add to separate "Bedroom 1" if possible? 
            // OR should it add to "Cart"? 
            // User said: "First we'll have one cart that is the always the active cart." 
            // This implies "Cart" is the collector. 
            // So if I am viewing "Bedroom 1", can I add anything to it?
            // "Then this cart will have another option to move it's contents to a selection."
            // Implies flow is Cart -> Selection. Not direct to Selection.
            // So activeCart ref locally should probably ALWAYS be the "Cart" (is_selected=true) upon load?
            // And "viewing" might need a separate ref if we want to just look at others?
            // But existing UI binds everything to `activeCart`. Refactoring entire UI to split `activeCart` vs `displayedCart` is risky.
            // Simplest path: activeCart IS the displayed cart. 
            // But we must assume `addItem` from catalog pages (which uses this composable) might be looking for "Cart".
            // If this composable is used on Catalog Page, it initializes, fetches carts, sets activeCart = Cart (is_selected). Adds item. Correct.
            // If this composable is used on Manager Page, it initializes, sets activeCart = Cart. User clicks "Bedroom 1". activeCart = Bedroom 1.
            // There is no "Add Item From Catalog" on Manager Page usually. Only "Add Web Link".
            // If User adds Web Link to Bedroom 1, is that allowed? Probably harmless.

            // So we stick to: activeCart = displayed cart.
            // Initial load finds the server-selected one (Cart).

                // Check for hash selectionID
                const hashParams = new URLSearchParams(window.location.hash.substring(1));
                const selectionId = hashParams.get('selectionID');
                if (selectionId) {
                    const targetCart = carts.value.find(c => c.id == selectionId);
                    if (targetCart) {
                        activeCart.value = targetCart;
                    }
                }

                if (!activeCart.value) {
                    if (appMode.value === 'selection') {
                        if (savedSelections.value.length > 0) {
                            // In Selection mode, prefer the first Saved Selection
                            activeCart.value = savedSelections.value[0];
                        } else {
                            // Auto-create if empty
                            await createCart('My Collection');
                            return; // createCart triggers another fetchCarts, so we stop here
                        }
                    } else {
                        // Default behavior (Active/Server Selected Cart)
                        const serverSelected = carts.value.find(c => c.is_selected);
                        if (serverSelected) {
                            activeCart.value = serverSelected;
                        } else if (carts.value.length > 0) {
                            activeCart.value = carts.value[0];
                        }
                    }
                } else {
                    // Refresh currently viewed cart data
                    const current = carts.value.find(c => c.id === activeCart.value.id);
                    if (current) {
                        activeCart.value = current;
                    } else {
                        // Fallback if deleted
                        if (appMode.value === 'selection' && savedSelections.value.length > 0) {
                            activeCart.value = savedSelections.value[0];
                        } else {
                            activeCart.value = carts.value.find(c => c.is_selected) || carts.value[0] || null;
                        }
                    }
                }
            }

            // A fresh read is the truth for every item no request is still settling.
            clearSettledFavorites();
            updateLocalCount(favoritesItemCount.value);

            // Auto-update out-of-stock items notes
            carts.value.forEach(cart => {
                if (!cart.items) return;
                cart.items.forEach(item => {
                    if (item.item_type === 'block' && item.total_slabs === 0) {
                        const oosNote = "This item is not available anymore";
                        if (!item.notes || !item.notes.includes(oosNote)) {
                            const newNote = item.notes ? item.notes + '\n\n' + oosNote : oosNote;
                            item.notes = newNote;
                            fetchApi(`/carts/${cart.id}/items/${item.id}`, {
                                method: 'PATCH',
                                body: JSON.stringify({ notes: newNote })
                            }).catch(e => console.error("Failed to auto-update out of stock note", e));
                        }
                    }
                });
            });

            nextTick(() => {
                initSortable();
                autoResizeTextarea(cartNotesTextarea.value);
                initCollectionObserver();
            });

        } catch (e) {
            console.error("Failed to load carts", e);

            if (throwOnError) {
                throw e;
            }
        } finally {
            if (append) {
                loadingMoreCollections.value = false;
            } else {
                loading.value = false;
            }
        }
    };

    const loadMoreCollections = async () => {
        if (appMode.value !== 'selection' || !hasMoreCollections.value || loadingMoreCollections.value || loading.value) return;

        await fetchCarts({ append: true });
    };

    const fetchCollectionOptions = async () => {
        try {
            const options = await fetchApi('/carts/selection-options');
            collectionOptions.value = Array.isArray(options) ? options : [];
        } catch (e) {
            console.error("Failed to load collection options", e);
            collectionOptions.value = [];
        }
    };

    const createCart = async (name) => {
        if (!name) return;
        loading.value = true;
        try {
            // This creates an inactive selection. The client_id lets a replayed
            // create resolve to the same collection; the API only accepts a UUID.
            const clientId = newUuid();
            const response = await writeApi('/carts', {
                method: 'POST',
                body: JSON.stringify({ name, ...(isUuid(clientId) ? { client_id: clientId } : {}) })
            });

            await fetchCarts();

            // View the new selection
            if (response && response.id) {
                viewCart(response.id);
            }
        } catch (e) {
            console.error("Failed to create cart", e);
        } finally {
            loading.value = false;
        }
    };

    const renameCart = async (cartId, name) => {
        if (!name || isReadOnly.value) return;
        try {
            await fetchApi(`/carts/${cartId}`, {
                method: 'PATCH',
                body: JSON.stringify({ name })
            });
            await fetchCarts();
        } catch (e) {
            console.error("Failed to rename cart", e);
        }
    };

    const deleteCart = async (cartId) => {
        if (!confirm('Are you sure you want to delete this collection?')) return;

        try {
            await fetchApi(`/carts/${cartId}`, {
                method: 'DELETE'
            });

            // If we deleted the active view, switch to main Cart
            if (activeCart.value && activeCart.value.id === cartId) {
                activeCart.value = null; // fetchCarts will reset to default
            }

            await fetchCarts();
        } catch (e) {
            console.error("Failed to delete cart", e);
        }
    };

    // Shared (read-only) collections opened from a share link
    const sharedCollections = ref(loadSharedCollections());
    const showOpenSharedModal = ref(false);
    const sharedLinkInput = ref('');
    const sharedLinkError = ref('');
    const openingSharedLink = ref(false);
    const sidebarTab = ref('mine'); // 'mine' or 'shared'

    const setSharedHash = (token) => {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        hashParams.delete('selectionID');
        hashParams.set('shared', token);
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}#${hashParams.toString()}`);
    };

    const clearSharedHash = () => {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        if (!hashParams.has('shared')) return;

        hashParams.delete('shared');
        const hash = hashParams.toString();
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ''}`);
    };

    const openOpenSharedModal = () => {
        sharedLinkInput.value = '';
        sharedLinkError.value = '';
        showOpenSharedModal.value = true;
    };

    const closeOpenSharedModal = () => {
        showOpenSharedModal.value = false;
        sharedLinkError.value = '';
    };

    /**
     * Opens a collection from a share link (or bare token) in read-only mode.
     * The owner opening their own link gets the normal editable view instead.
     *
     * @returns {Promise<boolean>} Whether the collection was opened.
     */
    const openSharedCollection = async (input) => {
        const token = parseShareToken(input);
        if (!token) {
            sharedLinkError.value = "That doesn't look like a share link.";
            return false;
        }

        openingSharedLink.value = true;
        loading.value = true;
        sharedLinkError.value = '';
        // Supersede any in-flight collection load so it can't replace this view.
        const requestId = ++cartDetailsRequestId;

        try {
            const data = await fetchApi(`/favorites-public/${token}`);
            if (requestId !== cartDetailsRequestId) return false;

            if (data?.is_owner && data.cart?.id) {
                clearSharedHash();
                sidebarTab.value = 'mine';
                await loadCartDetails(data.cart.id);
            } else {
                const items = Array.isArray(data?.items) ? data.items : [];
                activeCart.value = {
                    ...data.cart,
                    items,
                    items_count: items.length,
                    is_selected: false,
                    is_read_only: true,
                    share_token: token,
                };
                editingName.value = false;
                sharedCollections.value = rememberSharedCollection(sharedCollections.value, { token, name: data.cart?.name });
                setSharedHash(token);
                sidebarTab.value = 'shared';
            }

            showOpenSharedModal.value = false;
            sharedLinkInput.value = '';
            nextTick(() => initSortable());

            return true;
        } catch (e) {
            console.error("Failed to open shared collection", e);
            sharedLinkError.value = "We couldn't open that shared collection. Check the link and try again.";

            return false;
        } finally {
            openingSharedLink.value = false;
            loading.value = false;
        }
    };

    const removeSharedCollection = (token) => {
        sharedCollections.value = forgetSharedCollection(sharedCollections.value, token);

        if (activeCart.value?.is_read_only && activeCart.value.share_token === token) {
            clearSharedHash();
            const fallback = savedSelections.value[0] || serverActiveCart.value;
            if (fallback) {
                viewCart(fallback.id);
            } else {
                activeCart.value = null;
            }
        }
    };

    // Renamed from selectCart to viewCart (local only)
    const viewCart = async (cartId) => {
        const cart = carts.value.find(c => c.id === cartId);
        clearSharedHash();

        if (appMode.value === 'selection' && (!cart?.items || cart.id !== activeCart.value?.id)) {
            loading.value = true;
            try {
                await loadCartDetails(cartId);
            } catch (e) {
                console.error("Failed to load collection", e);
            } finally {
                loading.value = false;
            }
        } else if (cart) {
            activeCart.value = cart;
            nextTick(() => autoResizeTextarea(cartNotesTextarea.value));
        }
    };

    const autoResizeTextarea = (target) => {
        const textarea = target?.target ?? target;
        if (!textarea) return;

        textarea.style.height = 'auto';
        textarea.style.height = `${Math.max(textarea.scrollHeight, 40)}px`;
    };

    const moveCartMode = ref('new'); // 'new' or 'existing'
    const moveCartId = ref(null);

    const moveToSelection = async () => {
        if (!activeCart.value) return;

        loading.value = true;
        try {
            const payload = {};

            if (moveCartMode.value === 'new') {
                payload.name = moveCartName.value;
            } else {
                if (!moveCartId.value) {
                    //alert("Please select a destination collection.");
                    loading.value = false;
                    return;
                }
                payload.destination_cart_id = moveCartId.value;
            }

            const response = await fetchApi('/carts/move-to-selection', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            moveCartName.value = '';
            moveCartId.value = null;
            showMoveModal.value = false;

            // Check for redirect meta tag
            const redirectUrl = document.querySelector('meta[name="redirect-after-copy"]')?.getAttribute('content');
            if (redirectUrl && response.destination_cart_id) {
                window.location.href = `${redirectUrl}#selectionID=${response.destination_cart_id}`;
                return;
            }

            await fetchCarts();
            //alert("Items moved successfully.");
        } catch (e) {
            console.error("Failed to move items", e);
            //alert("Failed to move items: " + (e.message || "Unknown error"));
        } finally {
            loading.value = false;
        }
    };

    const updateCartNotes = async (cartId, notes) => {
        if (isReadOnly.value) return;

        try {
            // Optimistic update
            if (activeCart.value && activeCart.value.id === cartId) {
                activeCart.value.notes = notes;
            }

            await fetchApi(`/carts/${cartId}`, {
                method: 'PATCH',
                body: JSON.stringify({ notes })
            });
            // We don't strictly need to re-fetch if we trust the optimistic update
        } catch (e) {
            console.error("Failed to update notes", e);
        }
    };

    const getShareLink = async (cartId) => {
        // Instant Share: Check if we already have the token in state
        if (activeCart.value && activeCart.value.id === cartId && activeCart.value.share_token) {
            const publicLinkBase = document.querySelector('meta[name="public-link"]')?.getAttribute('content');
            if (publicLinkBase) {
                const base = publicLinkBase.endsWith('/') ? publicLinkBase : `${publicLinkBase}/`;
                return `${base}${activeCart.value.share_token}`;
            }
        }

        try {
            const data = await fetchApi(`/carts/${cartId}/share`, {
                method: 'POST'
            });

            // Check for public link meta tag
            const publicLinkBase = document.querySelector('meta[name="public-link"]')?.getAttribute('content');

            if (publicLinkBase && data.share_token) {
                // Ensure base ends with / and token is appended
                const base = publicLinkBase.endsWith('/') ? publicLinkBase : `${publicLinkBase}/`;
                // Update local state to make next time instant
                if (activeCart.value && activeCart.value.id === cartId) {
                    activeCart.value.share_token = data.share_token;
                }
                return `${base}${data.share_token}`;
            }

            return data.share_url;
        } catch (e) {
            console.error("Failed to get share link", e);
            return null;
        }
    };

    // Share Notification State
    const showShareSuccess = ref(false);

    const shareCart = async (cartId) => {
        const url = await getShareLink(cartId);
        if (url) {
            try {
                await navigator.clipboard.writeText(url);
                showShareSuccess.value = true;
                setTimeout(() => {
                    showShareSuccess.value = false;
                }, 2000);
            } catch (err) {
                prompt("Copy this link:", url);
            }
        }
    };

    const shareCartRichText = async (cartId) => {
        const url = await getShareLink(cartId);
        if (url) {
            try {
                const cart = carts.value.find(c => c.id === cartId) || activeCart.value;
                const cartName = cart ? cart.name : 'Collection';

                const html = `<a href="${url}">Click to view collection - ${cartName}</a>`;
                const blobHtml = new Blob([html], { type: 'text/html' });

                const item = new ClipboardItem({
                    'text/html': blobHtml
                });

                await navigator.clipboard.write([item]);
                showShareSuccess.value = true;
                setTimeout(() => {
                    showShareSuccess.value = false;
                }, 2000);
            } catch (err) {
                // Fallback to plain text if ClipboardItem or text/html is not supported
                try {
                    console.log("Fallback to plain text");
                    await navigator.clipboard.writeText(url);
                    showShareSuccess.value = true;
                    setTimeout(() => {
                        showShareSuccess.value = false;
                    }, 2000);
                } catch (e) {
                    prompt("Copy this link:", url);
                }
            }
        }
    };

    const copyToClipboard = async (text, event) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            if (event && event.currentTarget) {
                const btn = event.currentTarget;
                const clipboardIcon = btn.querySelector('svg.clipboard');
                const checkIcon = btn.querySelector('svg.check');
                
                if (clipboardIcon && checkIcon) {
                    clipboardIcon.classList.add('hidden');
                    checkIcon.classList.remove('hidden');
                    setTimeout(() => {
                        clipboardIcon.classList.remove('hidden');
                        checkIcon.classList.add('hidden');
                    }, 2000);
                }
            }
        } catch (err) {
            console.error('Failed to copy text: ', err);
            prompt("Copy this text:", text);
        }
    };

    // ... share logic ... (keep getShareLink, shareCart as is, verify line numbers)
    // Wait, getShareLink, shareCart are lines 264-315. I need to be careful with range.
    // I will skip replacing share/notes/delete if they are fine.
    // I need to only replace fetchCarts through addItem.

    // Let's replace specifically the block containing fetchCarts, createCart, renameCart, deleteCart, selectCart...

    // I'll adjust the range to cover from `const fetchCarts` (line 149) down to `const addItem`.

    const addItem = async (itemType, itemId, quantity = 1, cartId = null) => {
        // Trust activeCart is set (by ensure logic in fetchCarts/init)
        // If loaded on a page without sidebar, we rely on fetchCarts finding the default Cart.

        if (!activeCart.value) {
            // Retry fetch? Or wait?
            // If activeCart is null, fetchCarts might be in progress or failed.
            // If we blindly add, it fails.
            // Let's try to ensure we have carts.
            if (carts.value.length === 0) {
                await fetchCarts();
            }
        }

        if (!activeCart.value) {
            console.error("No active cart found to add to.");
            return { mutationSucceeded: false, refreshed: false, error: new Error('No active cart found.') };
        }

        const targetCartId = cartId ?? activeCart.value.id;

        let mutationResponse;

        try {
            mutationResponse = await writeApi(`/carts/${targetCartId}/items`, {
                method: 'POST',
                body: JSON.stringify({ item_type: itemType, item_id: itemId, quantity })
            });
        } catch (e) {
            console.error("Failed to add item", e);

            return { mutationSucceeded: false, refreshed: false, error: e };
        }

        // The write hands back the cart it touched, so there is normally nothing to re-read.
        if (applyCartFromWrite(mutationResponse?.cart)) {
            return { mutationSucceeded: true, refreshed: true, item: mutationResponse?.item ?? null, error: null };
        }

        try {
            await fetchCarts({ throwOnError: true });

            return { mutationSucceeded: true, refreshed: true, item: mutationResponse?.item ?? null, error: null };
        } catch (e) {
            console.error("Item was added, but carts could not be refreshed", e);

            return { mutationSucceeded: true, refreshed: false, item: mutationResponse?.item ?? null, error: e };
        }
    };

    // Add/Edit Web Link Modal State
    const showAddLinkModal = ref(false);
    const addCustomItemMode = ref(false); // Mode for custom item (single text + image)
    const showLoginPrompt = ref(false);

    // ... (other state)

    const newLinkUrl = ref('');
    const newLinkTitle = ref('');
    const newLinkPhoto = ref(null);
    const editingExternalId = ref(null); // ID of item being edited, null if adding

    const openAddLinkModal = () => {
        // Guest access allowed
        editingExternalId.value = null;
        addCustomItemMode.value = false;
        newLinkUrl.value = '';
        newLinkTitle.value = '';
        newLinkPhoto.value = null;
        showAddLinkModal.value = true;
    };

    const openAddCustomItemModal = () => {
        // Guest access allowed
        editingExternalId.value = null;
        addCustomItemMode.value = true;
        newLinkUrl.value = '';
        newLinkTitle.value = '';
        newLinkPhoto.value = null;
        showAddLinkModal.value = true;
    };

    const openEditLinkModal = (item) => {
        editingExternalId.value = item.id;
        if (item.item_type === 'custom') {
            addCustomItemMode.value = true;
            newLinkUrl.value = '';
        } else {
            addCustomItemMode.value = false;
            newLinkUrl.value = item.redirect_url; // Used as display or read-only
        }
        newLinkTitle.value = item.name;
        newLinkPhoto.value = null; // Don't pre-fill file input
        showAddLinkModal.value = true;
    };

    const closeAddLinkModal = () => {
        showAddLinkModal.value = false;
        newLinkUrl.value = '';
        newLinkTitle.value = '';
        newLinkPhoto.value = null;
        editingExternalId.value = null;
    };

    const submitExternalLink = async () => {
        if (!addCustomItemMode.value && !newLinkUrl.value) return;
        if (addCustomItemMode.value && (!newLinkTitle.value || (!newLinkPhoto.value && !editingExternalId.value))) {
            //alert("Please provide both title and image.");
            return;
        }

        loading.value = true;

        try {
            if (editingExternalId.value) {
                await updateExternalItem(editingExternalId.value, newLinkTitle.value, newLinkPhoto.value);
            } else {
                if (addCustomItemMode.value) {
                    await addCustomItem(newLinkTitle.value, newLinkPhoto.value);
                } else {
                    await addExternalItem(newLinkUrl.value, newLinkTitle.value, newLinkPhoto.value);
                }
            }
            closeAddLinkModal();
        } catch (e) {
            console.error("Failed to submit external link", e);
        } finally {
            loading.value = false;
        }
    };

    const addExternalItem = async (url, title = null, photoFile = null) => {
        // The API creates the favourites cart on first read; never invent one here.
        if (!activeCart.value) {
            await fetchCarts();
            if (!activeCart.value) return;
        }

        const externalUrl = url || prompt("Enter the web link URL:");
        if (!externalUrl) return;

        try {
            const formData = new FormData();
            formData.append('item_type', 'external');
            formData.append('external_url', externalUrl);
            formData.append('quantity', 1);

            if (title) {
                formData.append('manual_title', title);
            }

            if (photoFile) {
                formData.append('manual_image', photoFile);
            }

            await fetchApi(`/carts/${activeCart.value.id}/items`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Content-Type': undefined
                }
            });

            await fetchCarts();
        } catch (e) {
            console.error("Failed to add external item", e);
            //alert("Failed to add link.");
        }
    };

    const addCustomItem = async (title, photoFile) => {
        // The API creates the favourites cart on first read; never invent one here.
        if (!activeCart.value) {
            await fetchCarts();
            if (!activeCart.value) return;
        }

        if (!title || !photoFile) return;

        try {
            const formData = new FormData();
            formData.append('item_type', 'custom');
            formData.append('quantity', 1);
            formData.append('manual_title', title);
            formData.append('manual_image', photoFile);

            await fetchApi(`/carts/${activeCart.value.id}/items`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Content-Type': undefined
                }
            });

            await fetchCarts();
        } catch (e) {
            console.error("Failed to add custom item", e);
        }
    };

    const updateExternalItem = async (itemId, title, photoFile) => {
        if (!activeCart.value) return;

        try {
            const formData = new FormData();
            formData.append('_method', 'PATCH'); // Spoof PATCH for PHP multipart support

            if (title) {
                formData.append('manual_title', title);
            }

            if (photoFile) {
                formData.append('manual_image', photoFile);
            }

            // We use POST with _method=PATCH to support file uploads on "update"
            await fetchApi(`/carts/${activeCart.value.id}/items/${itemId}`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Content-Type': undefined
                }
            });

            await fetchCarts();
        } catch (e) {
            console.error("Failed to update external item", e);
            //alert("Failed to update link.");
        }
    };

    const removeItem = async (cartItemId, cartId = null) => {
        if (!activeCart.value && cartId === null) {
            return { mutationSucceeded: false, refreshed: false, error: new Error('No active cart found.') };
        }

        const targetCartId = cartId ?? activeCart.value.id;

        let mutationResponse;

        try {
            mutationResponse = await writeApi(`/carts/${targetCartId}/items/${cartItemId}`, {
                method: 'DELETE'
            });

            const originatingCart = carts.value.find(cart => cart.id === targetCartId)
                || (activeCart.value?.id === targetCartId ? activeCart.value : null);

            if (originatingCart?.items) {
                originatingCart.items = originatingCart.items.filter(item => item.id !== cartItemId);
            }
        } catch (e) {
            console.error("Failed to remove item", e);

            return { mutationSucceeded: false, refreshed: false, error: e };
        }

        if (applyCartFromWrite(mutationResponse?.cart)) {
            return { mutationSucceeded: true, refreshed: true, error: null };
        }

        try {
            await fetchCarts({ throwOnError: true });

            return { mutationSucceeded: true, refreshed: true, error: null };
        } catch (e) {
            console.error("Item was removed, but carts could not be refreshed", e);

            return { mutationSucceeded: true, refreshed: false, error: e };
        }
    };

    const updateItemQuantity = async (cartItemId, quantity) => {
        if (!activeCart.value) return;

        try {
            // Optimistic update
            const item = activeCart.value.items.find(i => i.id === cartItemId);
            if (item) {
                item.quantity = quantity;
            }

            await fetchApi(`/carts/${activeCart.value.id}/items/${cartItemId}`, {
                method: 'PATCH',
                body: JSON.stringify({ quantity })
            });
            // Removed await fetchCarts() to prevent UI jumping while editing
        } catch (e) {
            console.error("Failed to update item quantity", e);
            //alert("Failed to update quantity: " + (e.message || "Unknown error"));
            // Revert or re-fetch on error might be good, but for now simple error logging
        }
    };

    const updateItemNote = async (cartItemId, note) => {
        if (!activeCart.value || isReadOnly.value) return;

        try {
            // Optimistic update
            const item = activeCart.value.items.find(i => i.id === cartItemId);
            if (item) {
                item.notes = note;
            }

            await fetchApi(`/carts/${activeCart.value.id}/items/${cartItemId}`, {
                method: 'PATCH',
                body: JSON.stringify({ notes: note })
            });
            // Removed await fetchCarts() to prevent UI jumping while editing
        } catch (e) {
            console.error("Failed to update item note", e);
            //alert("Failed to update note.");
        }
    };

    /** The favourites cart: the server's selected cart, or the one on screen before it loads. */
    const favoritesCart = () => serverActiveCart.value || activeCart.value || null;

    const findFavoriteItem = (itemType, itemId) => (favoritesCart()?.items || []).find(
        item => item.item_type === itemType && String(item.item_id) === String(itemId)
    ) || null;

    const isFavorite = (itemType, itemId) => {
        const key = favoriteKey(itemType, itemId);
        if (key && optimisticFavorites.value.has(key)) return optimisticFavorites.value.get(key);
        // Until the favourites cart has loaded, the last known answer beats an empty heart.
        if (!favoritesCart() && cachedFavoriteKeys) return cachedFavoriteKeys.has(key);

        return Boolean(findFavoriteItem(itemType, itemId));
    };

    /** Whether a favourite write for this item is still settling. */
    const isFavoritePending = (itemType, itemId) => favoriteOps.has(favoriteKey(itemType, itemId));

    /** Drops overlay entries no request is settling, once a fresh read has landed. */
    const clearSettledFavorites = () => {
        if (optimisticFavorites.value.size === 0) return;

        const next = new Map([...optimisticFavorites.value].filter(([key]) => favoriteOps.has(key)));
        if (next.size !== optimisticFavorites.value.size) optimisticFavorites.value = next;
    };

    /**
     * Reconciles one item against the live favourites cart.
     *
     * @returns {Promise<{refreshFailed: boolean}>}
     */
    const syncFavorite = async (itemType, itemId, shouldBeFavorite) => {
        if (!favoritesCart()) await loadFavorites();

        const cart = favoritesCart();
        if (!cart) throw new Error('No favourites list is available yet.');

        const existing = findFavoriteItem(itemType, itemId);
        if (shouldBeFavorite === Boolean(existing)) return { refreshFailed: false };

        const written = shouldBeFavorite
            ? await writeApi(`/carts/${cart.id}/items`, {
                method: 'POST',
                body: JSON.stringify({ item_type: itemType, item_id: itemId, quantity: 1 })
            })
            : await writeApi(`/carts/${cart.id}/items/${existing.id}`, { method: 'DELETE' });

        if (applyCartFromWrite(written?.cart)) return { refreshFailed: false };

        // The write committed; a failed re-read is a stale view, never a failed favourite.
        try {
            await fetchCarts({ throwOnError: true });
            return { refreshFailed: false };
        } catch (e) {
            return { refreshFailed: true };
        }
    };

    /** Keeps reconciling until the server matches the most recently requested state. */
    const drainFavoriteOp = async (op) => {
        let synced = null;
        while (op.desired !== synced) {
            const target = op.desired;
            const outcome = await syncFavorite(op.itemType, op.itemId, target);
            synced = target;
            op.refreshFailed = outcome.refreshFailed;
            // Without a fresh read the local item ids are stale, so another pass
            // could not resolve a queued flip anyway. Stop on what was written.
            if (op.refreshFailed) break;
        }
    };

    /**
     * Sets an item's favourite state, optimistically and without duplicate
     * writes. Never rejects: a failure drops the optimistic state, which rolls
     * the heart back, and is reported through the result.
     *
     * @returns {Promise<{status: 'saved'|'saved-refresh-failed'|'failed'|'ignored', favorite?: boolean, error?: Error}>}
     */
    const setFavorite = (itemType, itemId, desired) => {
        const key = favoriteKey(itemType, itemId);
        if (!key) return Promise.resolve({ status: 'ignored' });

        const target = Boolean(desired);
        setOptimisticFavorite(key, target);

        const running = favoriteOps.get(key);
        if (running) {
            running.desired = target;
            return running.promise;
        }

        const op = { itemType, itemId, desired: target, refreshFailed: false };
        op.promise = drainFavoriteOp(op)
            .then(() => ({ status: op.refreshFailed ? 'saved-refresh-failed' : 'saved', favorite: op.desired }))
            .catch(error => {
                console.error('Failed to update favourites', error);
                return { status: 'failed', error };
            })
            .finally(() => {
                if (favoriteOps.get(key) === op) favoriteOps.delete(key);
                // A committed write whose refresh failed keeps its optimistic entry:
                // it is the only record of the truth until a read succeeds.
                if (!op.refreshFailed) setOptimisticFavorite(key, null);
            });

        favoriteOps.set(key, op);
        return op.promise;
    };

    /**
     * Resolves the intended state against the pending request rather than the
     * rendered one, so a fast double tap cannot read its own stale result.
     */
    const toggleFavorite = (itemType, itemId) => {
        const running = favoriteOps.get(favoriteKey(itemType, itemId));
        const current = running ? running.desired : isFavorite(itemType, itemId);

        return setFavorite(itemType, itemId, !current);
    };

    // Sorting Logic
    let gridSortable = null;
    let listSortable = null;
    const gridContainer = ref(null);
    const listContainer = ref(null);

    const initSortable = () => {
        // Init Grid
        if (gridContainer.value && !gridSortable) {
            gridSortable = new Sortable(gridContainer.value, {
                animation: 150,
                disabled: isReadOnly.value,
                onEnd: handleSortEnd
            });
        }

        // Init List
        if (listContainer.value && !listSortable) {
            listSortable = new Sortable(listContainer.value, {
                animation: 150,
                disabled: isReadOnly.value,
                onEnd: handleSortEnd
            });
        }
    };

    const handleSortEnd = async (evt) => {
        if (!activeCart.value || isReadOnly.value) return;

        const container = evt.to; // The container element
        const items = Array.from(container.children).map(el => el.dataset.id).filter(id => id);

        if (items.length === 0) return;

        try {
            await fetchApi(`/carts/${activeCart.value.id}/items/reorder`, {
                method: 'PATCH',
                body: JSON.stringify({ items })
            });
            // Update local state is tricky because Vue re-rendering might conflict with Sortable DOM manipulation.
            // But since we persist to DB and then fetchCarts...
            // Actually, fetching carts will re-render list and might reset order if not synced fast enough?
            // Sortable has already moved DOM. 
            // We should re-fetch to ensure sync.
            // await fetchCarts(); // Ideally yes, but might cause jump?
            // Let's assume server is truth
        } catch (e) {
            console.error("Sort failed", e);
            // Revert logic? Sortable doesn't easily revert.
            // We can just re-fetch carts to reset UI to server state
            await fetchCarts();
        }
    };

    // Download State
    const downloadingExcel = ref(false);
    const downloadingPdf = ref(false);

    const downloadExcel = async () => {
        if (!activeCart.value) return;

        const apiToken = getApiToken();
        const apiBase = document.querySelector('meta[name="api-url"]')?.getAttribute('content') || '/api';
        const baseUrl = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
        const url = isReadOnly.value
            ? `${baseUrl}/favorites-public/${activeCart.value.share_token}/export`
            : `${baseUrl}/carts/${activeCart.value.id}/export`;

        downloadingExcel.value = true;

        try {
            const headers = {};
            if (apiToken) headers['Authorization'] = `Bearer ${apiToken}`;
            if (guestSessionId.value) headers['X-Guest-Session'] = guestSessionId.value;

            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;

            // Try to get filename from content-disposition
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `Collection - ${activeCart.value.name}.xlsx`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch.length === 2)
                    filename = filenameMatch[1];
            }

            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (e) {
            console.error("Download failed", e);
        } finally {
            downloadingExcel.value = false;
        }
    };

    const downloadPdf = async () => {
        if (!activeCart.value) return;

        const apiBase = document.querySelector('meta[name="api-url"]')?.getAttribute('content') || '/api';
        const baseUrl = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
        const qtyParam = showQty.value ? '?show_qty=1' : '';
        const url = isReadOnly.value
            ? `${baseUrl}/favorites-public/${activeCart.value.share_token}/export-pdf${qtyParam}`
            : `${baseUrl}/carts/${activeCart.value.id}/export-pdf${qtyParam}`;

        downloadingPdf.value = true;
        try {
            const apiToken = getApiToken();
            const headers = {};
            if (apiToken) headers['Authorization'] = `Bearer ${apiToken}`;
            if (guestSessionId.value) headers['X-Guest-Session'] = guestSessionId.value;

            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;

            const cleanName = (activeCart.value.name || 'selection').replace(/[^a-z0-9]/gi, '_').toLowerCase();
            a.download = `Collection - ${cleanName}.pdf`;

            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (e) {
            console.error("PDF Download failed", e);
            alert("Failed to download PDF.");
        } finally {
            downloadingPdf.value = false;
        }
    };

    // Watchers
    watch(favoritesItemCount, (count) => {
        updateLocalCount(count);
    });

    // Remember the server's favourites (never unsettled taps) for the next page load.
    watch(() => serverActiveCart.value?.items, (items) => {
        if (!items) return;

        writeCachedFavoriteKeys(items.map(item => favoriteKey(item.item_type, item.item_id)).filter(Boolean));
    });

    watch(viewMode, () => {
        nextTick(() => {
            // Re-init sortable whenever view mode changes because DOM elements might be re-created / v-if logic
            // Actually destruction of Sortable is needed if element was removed?
            // Since we use v-if/v-show, elements might be destroyed.
            // Reset instances
            if (gridSortable) { gridSortable.destroy(); gridSortable = null; }
            if (listSortable) { listSortable.destroy(); listSortable = null; }
            initSortable();
        });
    });

    watch(isReadOnly, (readOnly) => {
        gridSortable?.option('disabled', readOnly);
        listSortable?.option('disabled', readOnly);
    });

    watch(() => activeCart.value?.notes, () => {
        nextTick(() => autoResizeTextarea(cartNotesTextarea.value));
    });

    watch(selectionFilter, () => {
        if (appMode.value !== 'selection') return;

        clearTimeout(collectionSearchTimer);
        collectionSearchTimer = setTimeout(() => {
            collectionPage.value = 1;
            fetchCarts({ refreshActive: false });
        }, 300);
    });

    watch(selectionSort, () => {
        if (appMode.value !== 'selection') return;

        collectionPage.value = 1;
        fetchCarts({ refreshActive: false });
    });

    watch(showMoveModal, (isVisible) => {
        if (isVisible && moveCartMode.value === 'existing') {
            fetchCollectionOptions();
        }
    });

    watch(moveCartMode, (mode) => {
        if (showMoveModal.value && mode === 'existing' && collectionOptions.value.length === 0) {
            fetchCollectionOptions();
        }
    });

    // Only register the lifecycle hook when the composable runs inside a
    // component setup(); called standalone (e.g. from unit tests) there is no
    // instance to bind to and Vue logs a warning.
    if (getCurrentInstance()) {
        onBeforeUnmount(() => {
            collectionObserver?.disconnect();
            clearTimeout(collectionSearchTimer);
        });
    }

    const showEmptyCartModal = ref(false);

    const emptyCart = async () => {
        if (!activeCart.value || !activeCart.value.items || activeCart.value.items.length === 0) return;

        // confirm() removed, handled by modal

        try {
            loading.value = true;
            await fetchApi(`/carts/${activeCart.value.id}/items`, {
                method: 'DELETE'
            });
            await fetchCarts(); // Refresh to update UI
            showEmptyCartModal.value = false; // Close modal
        } catch (e) {
            console.error("Failed to empty cart", e);
            alert("Failed to empty collection.");
        } finally {
            loading.value = false;
        }
    };

    /**
     * Moves anything favourited as a guest into the signed-in account, then
     * retires the guest session. A failed merge keeps the session so the next
     * page load can retry it.
     */
    const mergeGuestCarts = async () => {
        if (!getApiToken() || !guestSessionId.value) return { merged: false };

        const sessionId = guestSessionId.value;

        try {
            const summary = await fetchApi(`/carts/guest-summary?guest_session_id=${encodeURIComponent(sessionId)}`);
            if (!getApiToken()) return { merged: false };

            const hasSomethingToMerge = Boolean(summary?.has_mergeable_content ?? (Number(summary?.total_items) || 0) > 0);
            if (hasSomethingToMerge) {
                await writeApi('/carts/merge', {
                    method: 'POST',
                    body: JSON.stringify({ guest_session_id: sessionId })
                });
            }

            localStorage.removeItem('favorites_guest_session');
            guestSessionId.value = null;

            if (hasSomethingToMerge) await fetchCarts();

            return { merged: hasSomethingToMerge };
        } catch (e) {
            console.warn('Guest favourites could not be merged into this account.', e);
            return { merged: false, error: e };
        }
    };

    // User Auth
    const fetchUser = async () => {
        const apiToken = getApiToken();
        if (!apiToken) return;

        try {
            // We use fetchApi which handles the token automatically
            const user = await fetchApi('/user');
            if (!user) return; // Bail if fetchApi returned early (e.g. 401 / logged out)
            currentUser.value = user;
        } catch (e) {
            console.error("Failed to fetch user", e);
        }
    };

    // Quantity visibility
    const showQty = ref(false);

    // Mode Logic
    const appMode = ref('favorites'); // 'favorites' or 'selection'
    const showSidebar = computed(() => appMode.value === 'selection');
    const showMainCartInSidebar = computed(() => appMode.value !== 'selection');
    const showActions = computed(() => appMode.value === 'selection');

    // Permission: can upload document files (PDF, Excel, PowerPoint, Word)
    const canUploadDocuments = computed(() => {
        if (!currentUser.value || !currentUser.value.permissions) return false;
        return currentUser.value.permissions.includes('upload_documents');
    });

    const canViewSpsLinks = computed(() => {
        if (!currentUser.value) return false;

        const roles = new Set();

        if (typeof currentUser.value.role === 'string' && currentUser.value.role.length > 0) {
            roles.add(currentUser.value.role.toLowerCase());
        }

        if (Array.isArray(currentUser.value.roles)) {
            currentUser.value.roles
                .filter((role) => typeof role === 'string' && role.length > 0)
                .forEach((role) => roles.add(role.toLowerCase()));
        }

        return Array.from(roles).some((role) => role !== 'guest' && role !== 'customer');
    });

    const shouldShowSpsLink = (item) => {
        return canViewSpsLinks.value && item?.item_type === 'block' && !!item?.sps_url;
    };

    // Document-type display helpers (labels, background + icon classes).
    const {
        documentLabel,
        documentLabelShort,
        documentBgClass,
        documentBgBaseClass,
        documentIconClass,
        documentTextClass,
        documentSubTextClass,
    } = useDocumentTypes();

    // Init
    const init = async () => {
        // Check for app-mode meta
        const modeMeta = document.querySelector('meta[name="app-mode"]')?.getAttribute('content');
        if (modeMeta) {
            appMode.value = modeMeta;
        }

        // Check for show-qty meta
        showQty.value = !!document.querySelector('meta[name="show-qty"]');

        await fetchCarts(); // Must complete first (handles token exchange)

        const sharedToken = new URLSearchParams(window.location.hash.substring(1)).get('shared');
        if (sharedToken) {
            openSharedCollection(sharedToken);
        }

        fetchUser();        // Uses the exchanged Passport token
        mergeGuestCarts();  // Uses the exchanged Passport token
    };

    const goToLogin = () => {
        const loginUrl = document.querySelector('meta[name="api-login"]')?.getAttribute('content');
        if (loginUrl) {
            window.location.href = loginUrl;
        }
    };

    const goToLogout = () => {
        const logoutUrlStr = document.querySelector('meta[name="api-logout"]')?.getAttribute('content');

        if (logoutUrlStr) {
            clearApiToken();
            window.location.href = logoutUrlStr;
            return;
        }
        console.error("No logout URL found");
    };

    return {
        // State
        activeCart,
        carts,
        cartNotesTextarea,
        loading,
        viewMode,
        cartCount,

        // Mode State
        appMode,
        showSidebar,
        showMainCartInSidebar,
        showActions,
        showQty,

        // Modal State
        showExternalModal,
        pendingExternalUrl,
        showAddLinkModal,
        showLoginPrompt, // Added
        showEmptyCartModal,

        // Link State
        newLinkUrl,
        newLinkTitle,
        newLinkPhoto,
        editingExternalId,

        // Computed
        serverActiveCart,
        isReadOnly,

        // Shared (read-only) collections
        sharedCollections,
        showOpenSharedModal,
        sharedLinkInput,
        sharedLinkError,
        openingSharedLink,
        sidebarTab,
        openOpenSharedModal,
        closeOpenSharedModal,
        openSharedCollection,
        removeSharedCollection,
        savedSelections,
        selectionFilter,
        selectionSort,
        hasMoreCollections,
        loadingMoreCollections,
        collectionScrollContainer,
        collectionLoadMoreTrigger,
        collectionOptions,
        canUploadDocuments,
        canViewSpsLinks,

        // Document display helpers
        documentLabel,
        documentLabelShort,
        documentBgClass,
        documentBgBaseClass,
        documentIconClass,
        documentTextClass,
        documentSubTextClass,

        // Methods
        viewCart, // Replaces selectCart
        createCart,
        addItem,
        removeItem,
        updateItemQuantity,
        updateItemNote,
        isFavorite,
        isFavoritePending,
        loadFavorites,
        setFavorite,
        toggleFavorite,
        favoritesItemCount,

        moveToSelection,

        // Export State
        moveCartName,
        moveCartMode,
        moveCartId,
        showMoveModal,
        showShareSuccess,
        shareCart,
        shareCartRichText,
        copyToClipboard,

        // New Link Methods
        openAddLinkModal,
        openAddCustomItemModal,
        addCustomItemMode,
        openEditLinkModal,
        closeAddLinkModal,
        submitExternalLink,
        handleLinkClick,
        shouldShowSpsLink,

        // Navigation Logic
        cancelNavigation,
        proceedNavigation,

        // Export
        downloadExcel,
        downloadPdf,
        downloadingExcel,
        downloadingPdf,
        emptyCart,

        // Merge Logic
        mergeGuestCarts,

        // Login Logic
        goToLogin,
        goToLogout,
        currentUser,
        isLoggedOut,

        // Other
        isOpen,
        editingName,
        gridContainer,
        listContainer,
        confirmNavigation,
        fetchCarts,
        loadMoreCollections,
        autoResizeTextarea,
        renameCart,
        updateCartNotes,
        deleteCart,
        addExternalItem,

        init
    };
}

/**
 * Create component definition
 */
export function createFavoritesHandlerComponent(props = {}) {
    return {
        setup() {
            console.log("Setting up Favorites Handler with Share support...");
            const handler = useFavoritesHandler(props);

            onMounted(() => {
                handler.init();
            });

            return handler;
        }
    };
}
